import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  makeStyles,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';

import { useTranslation } from 'react-i18next';

import 'translations/translations';
import { RoleEnumDto, StateEnumDto, UserModelDto } from '.generated/api';
import { ValidatedTextInput } from 'components/ValidatedTextInput';
import { organizationActions } from 'actions/organizationActions';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import OverlaySpinner from 'components/spinners/OverlaySpinner';

type DialogProps = {
  isOpen: boolean;
  handleClose: () => void;
  user?: UserModelDto;
};

interface ParamTypes {
  orgId: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '0.1rem 1rem',
    minWidth: '8.5rem',
  },
  alignRight: {
    textAlign: 'right',
  },
  noMarginBottom: {
    marginBottom: 0,
  },
  lastButton: {
    marginRight: 0,
    marginBottom: 0,
  },
  dialogContentHeigh: {
    height: '18rem',
  },
  dialogContentAuto: {
    height: 'auto',
  },
  error: {
    color: theme.palette.error.main,
    textAlign: 'right',
    fontSize: '0.8rem',
  },
}));

const CreateEditUserDialog: React.FC<DialogProps> = (props) => {
  const { isOpen, handleClose, user } = props;
  const { t } = useTranslation(['translations']);
  const classes = useStyles();
  const dispatch = useDispatch();
  const { orgId } = useParams<ParamTypes>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [missingEmail, setMissingEmail] = useState<boolean>(true);
  const [newRoles, setNewRoles] = useState<RoleEnumDto[]>([]);
  const [newUser, setNewUser] = useState<UserModelDto>({
    name: '',
    userEmail: '',
    phone: '',
    state: StateEnumDto.ACTIVE,
    roles: [],
    enableAutoEmails: true,
    emailConfirmed: false,
  });
  const [error, setError] = useState<string>();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value, name } = e.target;
    setNewUser((newUser) => ({ ...newUser, [name]: value }));
  };

  const handleChangeRoles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target;
    if (!checked) {
      setNewRoles(newRoles.filter((role) => role !== name));
    }
    if (checked) {
      setNewRoles(newRoles.filter((role) => role !== RoleEnumDto.NOT_SET));
      switch (name) {
        case RoleEnumDto.ORGANIZATION_ADMIN:
          setNewRoles((newRoles) => [
            ...newRoles,
            RoleEnumDto.ORGANIZATION_ADMIN,
          ]);
          break;
        case RoleEnumDto.SUPERVISOR:
          setNewRoles((newRoles) => [...newRoles, RoleEnumDto.SUPERVISOR]);
          break;
        case RoleEnumDto.REGISTRATOR:
          setNewRoles((newRoles) => [...newRoles, RoleEnumDto.REGISTRATOR]);
          break;
        case RoleEnumDto.SURVEYOR:
          setNewRoles((newRoles) => [...newRoles, RoleEnumDto.SURVEYOR]);
          break;
        case RoleEnumDto.INTEGRATION:
          setNewRoles((newRoles) => [...newRoles, RoleEnumDto.INTEGRATION]);
          break;
      }
    }
  };

  const handleEmailChange = (name: string, value: string, error: boolean) => {
    setNewUser((newUser) => ({ ...newUser, [name]: value }));

    if (!error && value !== '') {
      setMissingEmail(false);
    } else {
      setMissingEmail(true);
    }
  };

  const handleChangeEnableAutoEmails = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, checked } = e.target;
    setNewUser((newUser) => ({ ...newUser, [name]: checked }));
  };

  const addOrEditCallback = (error?: string) => {
    setIsLoading(false);
    if (error) {
      setError(error);
      return;
    }
    handleCloseDialog();
  };

  const handleCloseDialog = () => {
    setNewUser({
      name: user?.name || '',
      userEmail: user?.userEmail || '',
      phone: user?.phone || '',
      state: user?.state || StateEnumDto.ACTIVE,
      roles: user?.roles || [],
      enableAutoEmails:
        user?.enableAutoEmails !== undefined ? user.enableAutoEmails : true,
      emailConfirmed: (user as any)?.emailConfirmed || false,
    });
    setNewRoles(user?.roles || []);
    setError(undefined);

    handleClose();
  };

  const handleCreate = () => {
    setIsLoading(true);
    setError(undefined);
    if (user) {
      dispatch(
        organizationActions.editUserInOrganization(
          orgId,
          user.userEmail,
          newUser.name,
          newUser.phone,
          newRoles.length > 0 ? newRoles : [RoleEnumDto.NOT_SET],
          newUser.enableAutoEmails,
          addOrEditCallback,
        ),
      );
    } else {
      dispatch(
        organizationActions.addUserToOrganization(
          orgId,
          {
            name: newUser.name,
            userEmail: newUser.userEmail,
            phone: newUser.phone,
            state: newUser.state,
            roles: newRoles.length > 0 ? newRoles : [RoleEnumDto.NOT_SET],
            enableAutoEmails: newUser.enableAutoEmails,
            emailConfirmed: false,
          },

          addOrEditCallback,
        ),
      );
    }
  };

  useEffect(() => {
    if (user) {
      setNewUser(user);
      setNewRoles(user.roles);
      setMissingEmail(false);
      setError(undefined);
    }
  }, [user]);

  return (
    <Dialog
      disableBackdropClick={false}
      disableEscapeKeyDown={false}
      maxWidth="xs"
      open={isOpen}
      onClose={handleCloseDialog}
    >
      <OverlaySpinner isLoading={isLoading}>
        <DialogTitle disableTypography={true}>
          <Typography variant="h3">
            {user
              ? t('organization_page.users.dialog.title_edit')
              : t('organization_page.users.dialog.title_create')}
          </Typography>
        </DialogTitle>

        <DialogContent
          className={clsx(
            !user ? classes.dialogContentHeigh : classes.dialogContentAuto,
          )}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                type="text"
                variant="outlined"
                margin="none"
                fullWidth
                label={t('organization_page.users.labels.user_name')}
                name="name"
                value={newUser.name}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                type="text"
                variant="outlined"
                margin="none"
                fullWidth
                label={t('organization_page.users.labels.user_phone')}
                name="phone"
                value={newUser.phone}
                onChange={handleChange}
              />
            </Grid>
            {!user && (
              <Grid item xs={6}>
                <ValidatedTextInput
                  type="email"
                  required={false}
                  label={t('organization_page.users.labels.user_email')}
                  handleChangeCallback={handleEmailChange}
                  inputName="userEmail"
                  autoFocus={false}
                  margin="none"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newRoles.some(
                        (role) => role === RoleEnumDto.ORGANIZATION_ADMIN,
                      )}
                      onChange={handleChangeRoles}
                      name={RoleEnumDto.ORGANIZATION_ADMIN}
                      color="primary"
                    />
                  }
                  label={t(`enums.roles.${RoleEnumDto.ORGANIZATION_ADMIN}`)}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newRoles.some(
                        (role) => role === RoleEnumDto.SUPERVISOR,
                      )}
                      onChange={handleChangeRoles}
                      name={RoleEnumDto.SUPERVISOR}
                      color="primary"
                    />
                  }
                  label={t(`enums.roles.${RoleEnumDto.SUPERVISOR}`)}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newRoles.some(
                        (role) => role === RoleEnumDto.REGISTRATOR,
                      )}
                      onChange={handleChangeRoles}
                      name={RoleEnumDto.REGISTRATOR}
                      color="primary"
                    />
                  }
                  label={t(`enums.roles.${RoleEnumDto.REGISTRATOR}`)}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newRoles.some(
                        (role) => role === RoleEnumDto.SURVEYOR,
                      )}
                      onChange={handleChangeRoles}
                      name={RoleEnumDto.SURVEYOR}
                      color="primary"
                    />
                  }
                  label={t(`enums.roles.${RoleEnumDto.SURVEYOR}`)}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newRoles.some(
                        (role) => role === RoleEnumDto.INTEGRATION,
                      )}
                      onChange={handleChangeRoles}
                      name={RoleEnumDto.INTEGRATION}
                      color="primary"
                    />
                  }
                  label={t(`enums.roles.${RoleEnumDto.INTEGRATION}`)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      name="enableAutoEmails"
                      checked={newUser.enableAutoEmails}
                      onChange={handleChangeEnableAutoEmails}
                    />
                  }
                  label={t('organization_page.users.labels.enable_emails')}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <Typography className={classes.error}>{error}</Typography>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            color="secondary"
            className={classes.noMarginBottom}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreate}
            disabled={missingEmail || newRoles?.length === 0}
            className={classes.lastButton}
          >
            {user ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </OverlaySpinner>
    </Dialog>
  );
};

export { CreateEditUserDialog };
